(ns arthur.domain.paint
  "Small authored polygon operations, each on a named symbol. Paint nodes read
  their symbol's frames directly; a roto instance's exposure and picture sampling
  must not quantise a hand edit.

  A SHAPE'S ORIGIN IS THE MIDDLE OF WHAT IT DRAWS. Points arrive here in the
  space the node's `[:xform :pos]` lives in — that is what `nest/drawn-inside`
  hands over — and `centred` splits them into a ring about the origin and the
  `pos` that puts it back where it was drawn. See `centred`: it is the invariant
  the whole pivot story rests on, and this namespace is where it is established."
  (:require [arthur.domain.channel :as channel]))

(def geometry [:geom :pts])

(defn middle
  "The middle of the box round flat points `pts`, in their own space."
  [pts]
  (let [xs (take-nth 2 pts)
        ys (take-nth 2 (rest pts))]
    [(/ (+ (apply min xs) (apply max xs)) 2)
     (/ (+ (apply min ys) (apply max ys)) 2)]))

(defn centred
  "Flat points `pts`, given in the space a node's `pos` lives in, as
  `[ring pos]`: the same drawing about the origin, and the position that puts it
  back exactly where it was.

  THE ORIGIN OF A SHAPE IS THE MIDDLE OF WHAT IT DRAWS, so a shape needs no
  pivot of its own: `[:xform :pivot]` defaults to the node's origin, and for a
  drawing that IS the middle of the drawing. A stroke stored exactly as it was
  drawn would have its origin at the SYMBOL's origin, which on the stage is the
  top-left corner — 126 px away on a 320x200 stage, an orbit wider than the
  stage — and that is what every drawing would turn about with a default pivot
  and no centring here.

  IT IS WORTH DOING ANYWAY, NOW THAT THERE IS A PIVOT, because this is the half
  nobody has to choose. A pivot is a stored choice and a default has to be a good
  one: with the origin on the content the default is already right, a turn writes
  `rot` alone, and nothing is stored on the node for anybody to have to look at.
  A symbol instance is the case that cannot do this — its origin is its symbol's,
  and moving a symbol's origin would move every drawing inside it out from under
  everything that reads them — so it gets a pivot written where it is placed;
  see `clip/place-symbol`.

  Hand-authored scenes have always been written this way — `demo/scene.edn`'s
  card is `[-44 -30 44 -30 44 30 -44 30]` with its place in `pos` — so this is
  the paint tool joining the convention rather than a new one."
  [pts]
  (let [[cx cy] (middle pts)]
    [(into [] (map-indexed (fn [i v] (- v (if (even? i) cx cy)))) pts)
     [cx cy]]))

(defn shapes [clip sid]
  (->> (get-in clip [:symbols sid :nodes])
       (filter (fn [[_ node]] (:paint? node)))
       (sort-by (comp :z val))
       vec))

(defn active-frame [ch frame]
  (let [frames (sort (keys (:keys ch)))]
    (or (last (take-while #(<= % frame) frames)) (first frames))))

(defn new-shape
  "`clip` with a shape drawn at `points` — in the space the new node's `pos` will
  live in, which is the symbol's own coordinates — on frame `frame` of `sid`.

  THE RING IS CENTRED AND THE MIDDLE GOES IN `pos`, which is the whole of
  `centred`: the shape's origin is what it draws, so it turns and scales about
  itself with nothing stored and nothing solved. This is the one place every
  drawing is born — the pen, the brush, and each piece the eraser leaves — so it
  is the one place the invariant has to be established."
  [clip sid id frame points color]
  (let [end (get-in clip [:symbols sid :frames])
        z (str "z" (js/Date.now) "-" (name id))]
    (if (and (<= 0 frame) (< frame end) (>= (count points) 6)
             (even? (count points)))
      (let [[ring pos] (centred points)]
        (assoc-in clip [:symbols sid :nodes id]
                  {:id id :name (str "shape " (inc (count (shapes clip sid))))
                   :kind :poly :paint? true :parent nil :z z
                   :span [frame end]
                   :channels {geometry (channel/keyed {frame ring} :hold)
                              [:xform :pos] (channel/framed pos)
                              [:style :color] (channel/framed color)}}))
      clip)))

(defn add-key [clip sid id frame]
  (let [path [:symbols sid :nodes id]
        node (get-in clip path)
        ch (get-in node [:channels geometry])
        [start end] (:span node)]
    (if (and (:paint? node) (<= start frame) (< frame end) ch)
      (assoc-in clip (into path [:channels geometry :keys frame])
                ;; A drawing is authored and keyed, never dense, so there is
                ;; no tier-2 store to read it out of.
                (vec (channel/value-at ch frame nil)))
      clip)))

(defn set-vertex [clip sid id key-frame vertex [x y]]
  (let [path [:symbols sid :nodes id :channels geometry :keys key-frame]
        points (get-in clip path)
        i (* 2 vertex)]
    (if (and points (< (inc i) (count points)))
      (assoc-in clip path (-> points (assoc i x) (assoc (inc i) y)))
      clip)))

(defn- every-key
  "`f` over the points of every key of the shape's geometry."
  [clip sid id f]
  (let [path [:symbols sid :nodes id :channels geometry :keys]]
    (if (and (:paint? (get-in clip [:symbols sid :nodes id])) (map? (get-in clip path)))
      (update-in clip path update-vals f)
      clip)))

(defn insert-vertex
  "A new point after point `i`, a fraction `t` of the way along the edge to the
  next, on EVERY key: a point's index is what it is across keys, so a tween
  between two keys only means anything while they have the same points. The
  shape does not change on any key."
  [clip sid id i t]
  (every-key clip sid id
             (fn [pts]
               (let [n (quot (count pts) 2)
                     j (mod (inc i) n)
                     at #(+ (nth pts (+ (* 2 i) %))
                            (* t (- (nth pts (+ (* 2 j) %)) (nth pts (+ (* 2 i) %)))))]
                 (if (< i n)
                   (-> (subvec pts 0 (* 2 (inc i)))
                       (conj (at 0) (at 1))
                       (into (subvec pts (* 2 (inc i)))))
                   pts)))))

(defn delete-vertex
  "Point `i` gone from every key, as `insert-vertex` adds one. A triangle keeps
  its three."
  [clip sid id i]
  (every-key clip sid id
             (fn [pts]
               (if (and (< 6 (count pts)) (< (* 2 i) (count pts)))
                 (into (subvec pts 0 (* 2 i)) (subvec pts (* 2 (inc i))))
                 pts))))

(defn set-points
  "Key `key-frame` of the shape is `points`, whatever it held, IN THE NODE'S OWN
  COORDINATES: a cut writing back the piece it kept.

  The node's origin is left where it is, which is why this is the form a cut
  uses. Re-centring would have to move `pos` to compensate, and `pos` can be
  keyed and the drawing can be keyed, so there is no one middle to move it to —
  the only exact moment for that is while the transform is static, which is what
  `place-points` is for."
  [clip sid id key-frame points]
  (let [path [:symbols sid :nodes id :channels geometry :keys key-frame]]
    (if (get-in clip path)
      (assoc-in clip path points)
      clip)))

(defn place-points
  "Key `key-frame` of the shape is `points`, GIVEN IN THE SPACE ITS `pos` LIVES
  IN: centred as `new-shape` centres them, with `pos` moved to put them back.

  What a re-fit writes. Adjusting a brush stroke's fit re-traces the same stroke
  from the stage pixels it was painted in, so its points arrive in the same space
  they did when the shape was made, and writing them as the node's own would move
  the drawing by its own position. Only ever used on a shape a stroke has just
  made, which is why moving `pos` is exact here: nothing is keyed yet."
  [clip sid id key-frame points]
  (let [path [:symbols sid :nodes id :channels geometry :keys key-frame]]
    (if (get-in clip path)
      (let [[ring pos] (centred points)]
        (-> clip
            (assoc-in path ring)
            (assoc-in [:symbols sid :nodes id :channels [:xform :pos]]
                      (channel/framed pos))))
      clip)))
